/** Disable every optional data category supported by Sentry JavaScript v11. */
export const sentryDataCollection = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [] as [],
  urlQueryParams: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
  frameContextLines: 0,
}

/**
 * Tags that may leave the device or worker (2026-10-17). Every other tag is dropped. Values must be short codes
 * (SAFE_TAG_VALUE, never containing '@' or '%40'): screen or boundary names, route templates without ids, stage and
 * classification codes, HTTP status. `route` must also be a template (ROUTE_SEGMENT) — a concrete path is dropped.
 * The `service` tag is always overwritten with the caller's service name.
 * The API keeps its own copy (apps/api/src/sentry-privacy.ts) and must use the same rules.
 */
export const SENTRY_TAG_ALLOWLIST = [
  'error_boundary',
  'where',
  'platform',
  'route',
  'stage',
  'code',
  'kind',
  'http_status',
  'service',
  'legacy',
  'handled',
] as const

/** Context fields kept for diagnosis (platform, OS version, app version and build, device kind). */
const CONTEXT_FIELDS: Record<string, readonly string[]> = {
  os: ['name', 'version'],
  app: ['app_version', 'app_build'],
  device: ['family', 'model', 'simulator'],
}

/** No spaces, '@', query or fragment characters: codes, route templates and versions only. */
const SAFE_TAG_VALUE = /^[\w.:/+-]{1,100}$/
/** Never an e-mail address, plain or URL-encoded, even without spaces (kim@example.com, kim%40example.com). */
const EMAIL_LIKE = /@|%40/i
/**
 * One segment of a route template: a lower-case name (letters and hyphens, no digits), a `:param` or the `*` wildcard.
 * A raw path segment such as an id (`abc123`, `user_2x`, an e-mail) never matches, so `/users/abc123` is dropped
 * while `/users/:id` stays. Same rule as the app's endpointPattern (apps/mobile/src/lib/api-errors.ts).
 */
const ROUTE_SEGMENT = /^(?:[a-z][a-z-]{0,39}|:[A-Za-z_][A-Za-z0-9_]{0,39}|\*)$/
const SAFE_CODE = /^[\w.:-]{1,64}$/
const SAFE_CONTEXT_VALUE = /^[\w .,:()/+-]{1,100}$/
const DEBUG_ID =
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}(?:-[0-9a-f]{1,8})?$/i
/** Source files and React Native bundles (index.android.bundle, main.jsbundle, Hermes bytecode). */
const SOURCE_FILE = /^[\w.-]+\.(?:m?[jt]sx?|cjs|py|bundle|jsbundle|hbc)$/
const PLACEHOLDER_FILE = '[source]'
const PASSTHROUGH_FILES = new Set(['[native code]', 'native', '<anonymous>'])

/**
 * File name for a stack frame or a debug image. Only the base name is kept (no directories, query or hash, which
 * can carry account paths or tokens). React Native frames keep the app:/// scheme so the value stays a valid URL:
 * Sentry matches a frame's absolute path to debug_meta.images[].code_file exactly, and both go through this function.
 */
export function sentrySourceFile(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  if (PASSTHROUGH_FILES.has(raw)) return raw
  const path = raw.split(/[?#]/, 1)[0] ?? ''
  const base = path.split(/[\\/]/).pop() ?? ''
  if (!SOURCE_FILE.test(base)) return PLACEHOLDER_FILE
  return raw.startsWith('app:///') ? `app:///${base}` : base
}

function shortString(value: unknown, pattern: RegExp): string | undefined {
  const text = typeof value === 'number' || typeof value === 'boolean' ? String(value) : value
  return typeof text === 'string' && pattern.test(text) ? text : undefined
}

/**
 * A short code that is not an e-mail address (tag values and fingerprint parts). A value that starts with '/' is a
 * path and must be a route template, whichever tag or fingerprint part carries it.
 */
function safeLabel(value: unknown): string | undefined {
  if (typeof value === 'string' && value.startsWith('/')) return routeTemplate(value)
  const text = shortString(value, SAFE_TAG_VALUE)
  return text !== undefined && !EMAIL_LIKE.test(text) ? text : undefined
}

/** `/`, `/simulations/:id/retry`, `/images/*` — a template, never a concrete path. */
function routeTemplate(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 100 || !value.startsWith('/')) return undefined
  if (value === '/') return value
  const segments = value.slice(1).split('/')
  return segments.every((s) => ROUTE_SEGMENT.test(s)) ? value : undefined
}

function scrubTags(raw: unknown, service: string): Record<string, string> {
  const tags: Record<string, string> = {}
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  for (const name of SENTRY_TAG_ALLOWLIST) {
    const value = name === 'route' ? routeTemplate(source[name]) : safeLabel(source[name])
    if (value !== undefined) tags[name] = value
  }
  tags.service = service
  return tags
}

function scrubContexts(raw: unknown): Record<string, Record<string, unknown>> | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const source = raw as Record<string, unknown>
  const contexts: Record<string, Record<string, unknown>> = {}
  for (const [name, fields] of Object.entries(CONTEXT_FIELDS)) {
    const context = source[name]
    if (!context || typeof context !== 'object') continue
    const clean: Record<string, unknown> = {}
    for (const field of fields) {
      const value = (context as Record<string, unknown>)[field]
      if (typeof value === 'boolean') clean[field] = value
      else {
        const text = shortString(value, SAFE_CONTEXT_VALUE)
        if (text !== undefined) clean[field] = text
      }
    }
    if (Object.keys(clean).length > 0) contexts[name] = clean
  }
  return Object.keys(contexts).length > 0 ? contexts : undefined
}

/** Debug IDs connect minified frames to uploaded source maps. Only the id, the image type and the file name stay. */
function scrubDebugMeta(raw: unknown): { images: Record<string, string>[] } | undefined {
  const images = (raw as { images?: unknown } | undefined)?.images
  if (!Array.isArray(images)) return undefined
  const clean: Record<string, string>[] = []
  for (const image of images) {
    if (!image || typeof image !== 'object') continue
    const { type, debug_id: debugId, code_file: codeFile } = image as Record<string, unknown>
    if (typeof debugId !== 'string' || !DEBUG_ID.test(debugId)) continue
    const entry: Record<string, string> = { debug_id: debugId }
    const kind = shortString(type, SAFE_CODE)
    if (kind) entry.type = kind
    const file = sentrySourceFile(codeFile)
    if (file) entry.code_file = file
    clean.push(entry)
  }
  return clean.length > 0 ? { images: clean } : undefined
}

function scrubFrame(frame: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {}
  if (typeof frame.function === 'string') clean.function = frame.function.slice(0, 200)
  for (const key of ['lineno', 'colno'] as const) {
    if (typeof frame[key] === 'number') clean[key] = frame[key]
  }
  if (typeof frame.in_app === 'boolean') clean.in_app = frame.in_app
  const filename = sentrySourceFile(frame.filename)
  if (filename) clean.filename = filename
  return clean
}

function scrubMechanism(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const { type, handled } = raw as Record<string, unknown>
  const clean: Record<string, unknown> = {}
  const kind = shortString(type, SAFE_CODE)
  if (kind) clean.type = kind
  if (typeof handled === 'boolean') clean.handled = handled
  return Object.keys(clean).length > 0 ? clean : undefined
}

function scrubFingerprint(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const parts = raw.filter(
    (part): part is string =>
      typeof part === 'string' && (part === '{{ default }}' || safeLabel(part) !== undefined),
  )
  return parts.length === raw.length && parts.length > 0 ? parts : undefined
}

/**
 * Error-only telemetry. User data, AI content, URLs, request data, breadcrumbs, extra data and exception text are
 * excluded. Kept for diagnosis (none of it is personal data): release, dist, environment, debug ids, OS name and
 * version, app version and build, device family and model, error type, handled or not, stack frames (function,
 * file name, line, column) and the allow-listed tags above.
 */
export function scrubSentryEvent<T>(event: T, service: string): T {
  const raw = event as Record<string, unknown>
  const safe: Record<string, unknown> = { type: undefined }
  for (const key of ['event_id', 'timestamp', 'platform', 'level', 'sdk']) {
    if (raw[key] !== undefined) safe[key] = raw[key]
  }
  for (const key of ['release', 'dist', 'environment']) {
    const value = raw[key]
    if (typeof value === 'string' && value.length > 0 && value.length <= 200) safe[key] = value
  }
  safe.tags = scrubTags(raw.tags, service)
  const contexts = scrubContexts(raw.contexts)
  if (contexts) safe.contexts = contexts
  const debugMeta = scrubDebugMeta(raw.debug_meta)
  if (debugMeta) safe.debug_meta = debugMeta
  const fingerprint = scrubFingerprint(raw.fingerprint)
  if (fingerprint) safe.fingerprint = fingerprint
  const exception = raw.exception as { values?: Record<string, unknown>[] } | undefined
  if (exception?.values) {
    safe.exception = {
      values: exception.values.map((value) => {
        const stack = value.stacktrace as { frames?: Record<string, unknown>[] } | undefined
        const frames = (stack?.frames ?? []).map(scrubFrame)
        const mechanism = scrubMechanism(value.mechanism)
        return {
          type: typeof value.type === 'string' ? value.type.slice(0, 100) : 'Error',
          value: 'Application error (message withheld for privacy)',
          ...(mechanism ? { mechanism } : {}),
          // Sentry rejects an empty frames array. Keep stackless exceptions without inventing a stack.
          ...(frames.length > 0 ? { stacktrace: { frames } } : {}),
        }
      }),
    }
  }
  return safe as T
}
